import { useParams } from "react-router-dom";
import { useMemo } from "react";
import { parseRestaurantSegment } from "./restaurantUrl";

export interface UseRestaurantFromParamsResult {
  /** Resolved restaurant UUID from URL segment; null if segment missing or invalid */
  restaurantCorrelationId: string | null;
  /** Slug part of the segment (cosmetic only) */
  slug: string | undefined;
  /** If segment is invalid, redirect to this path; otherwise null. Caller should render <Navigate to={redirectTo} replace /> when non-null. */
  redirectTo: string | null;
}

/**
 * Reads restaurantSlugAndId from route params, parses it, and returns correlationId + optional redirect path.
 * Use in pages under /restaurants/:restaurantSlugAndId/... to get the UUID for API calls and auth.
 * When redirectTo is non-null, render <Navigate to={redirectTo} replace />.
 */
export function useRestaurantFromParams(): UseRestaurantFromParamsResult {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const parsed = useMemo(
    () => (restaurantSlugAndId ? parseRestaurantSegment(restaurantSlugAndId) : { correlationId: null }),
    [restaurantSlugAndId]
  );
  const redirectTo = parsed.correlationId == null ? "/my-restaurants" : null;
  return {
    restaurantCorrelationId: parsed.correlationId,
    slug: parsed.slug,
    redirectTo,
  };
}
